import React from 'react';
import { Check, Shield, Wallet, Users, BookOpen, Landmark, ThumbsUp, AlertTriangle } from 'lucide-react';
import { getStartupLegalForms, getEvolutionLegalForms } from '../../data/legalForms';

/**
 * LegalFormSelection — étape « Statut juridique » de CreateCompany.
 *
 * 14/06/2026 — Refonte lisibilité (demande utilisateur : « compact, dense,
 * manque de lisibilité ») : cartes empilées pleine largeur, critères alignés
 * en grille, colonnes Atouts / Points de vigilance explicites, textes
 * agrandis. La note pédagogique reste affichée sur la carte sélectionnée.
 */

// Atouts / vigilances synthétiques par statut (pédagogie lisible en un
// coup d'œil — complète les champs chiffrés du référentiel legalForms).
const FORM_HIGHLIGHTS = {
  auto: {
    pros: ['Création immédiate, zéro capital', 'Comptabilité ultra-allégée', 'Charges payées uniquement sur le CA encaissé (22 %)'],
    cons: ['Impossible d\'embaucher des chauffeurs salariés', 'Plafond 188 700 € de CA/an', 'Peu crédible en banque (taux +1 pt)'],
  },
  ei: {
    pros: ['Aucun capital requis', 'Patrimoine personnel protégé (loi 2022)', 'Gestion simple, idéale pour démarrer'],
    cons: ['Cotisations TNS ~40 % du bénéfice', 'Pas de personnalité morale', 'Crédibilité bancaire moyenne (taux +0,5 pt)'],
  },
  eurl: {
    pros: ['Responsabilité limitée au capital', 'Personnalité morale : rassure clients et banques', 'Option possible pour l\'IS'],
    cons: ['1 000 € de capital recommandé', 'Formalités de création plus lourdes', 'Cotisations TNS ~40 % sur rémunération'],
  },
};

export default function LegalFormSelection({ selected, onSelect }) {
  const startupForms = getStartupLegalForms();
  const evolutionForms = getEvolutionLegalForms();

  return (
    <div className="space-y-4" data-testid="step-legal-form">
      <div>
        <h3 className="text-xl font-bold text-foreground mb-1">Statut juridique</h3>
        <p className="text-sm text-slate-400 leading-relaxed">
          Le statut détermine votre capital de départ, vos cotisations sociales et votre crédibilité auprès des banques.
          Vous pourrez évoluer vers un statut plus structuré à mesure que votre activité grandit.
        </p>
      </div>

      <div className="space-y-3">
        {startupForms.map(lf => {
          const isSelected = selected === lf.id;
          const hl = FORM_HIGHLIGHTS[lf.id] || { pros: [], cons: [] };
          return (
            <button
              key={lf.id}
              type="button"
              onClick={() => onSelect(lf.id)}
              className={`relative w-full text-left rounded-xl border-2 p-5 transition-all ${
                isSelected
                  ? 'bg-ink-700/60 shadow-lg'
                  : 'bg-ink-800 border-ink-600 hover:border-slate-500 hover:bg-ink-700/30'
              }`}
              style={isSelected ? { borderColor: lf.color } : {}}
              data-testid={`legal-form-${lf.id}`}
            >
              {isSelected && (
                <div className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: lf.color }}>
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}

              {/* En-tête : icône + nom + description */}
              <div className="flex items-start gap-3">
                <span className="text-3xl leading-none">{lf.icon}</span>
                <div className="min-w-0 pr-8">
                  <p className="font-bold text-base" style={{ color: isSelected ? lf.color : '#E2E8F0' }}>
                    {lf.shortName}
                    <span className="text-xs font-normal text-slate-500 ml-2">{lf.fullLabel}</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed">{lf.description}</p>
                </div>
              </div>

              {/* Critères chiffrés — grille aérée */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                {[
                  {
                    icon: Wallet, color: 'text-amber-300', label: 'Capital',
                    value: (lf.capitalRecommended || lf.capitalMin) === 0
                      ? 'Aucun'
                      : `${(lf.capitalRecommended || lf.capitalMin).toLocaleString('fr-FR')} €`,
                  },
                  {
                    icon: Shield, color: lf.liabilityLimited ? 'text-emerald-300' : 'text-amber-300', label: 'Responsabilité',
                    value: lf.liabilityLimited ? 'Limitée au capital' : 'Patrimoine pro',
                  },
                  {
                    icon: Users, color: 'text-sky-300', label: 'Cotisations',
                    value: `${Math.round(lf.chargesSocialesPct * 100)} % (${lf.dirigeantRegime === 'tns' ? 'TNS' : 'Salarié'})`,
                  },
                  {
                    icon: Landmark, color: lf.banqueRateModifier <= 0 ? 'text-emerald-300' : 'text-rose-300', label: 'Taux bancaire',
                    value: lf.banqueRateModifier === 0 ? 'Neutre' : `${lf.banqueRateModifier > 0 ? '+' : ''}${lf.banqueRateModifier} pt`,
                  },
                ].map(crit => (
                  <div key={crit.label} className="bg-ink-900/50 border border-ink-700 rounded-lg px-2.5 py-2">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1">
                      <crit.icon className={`w-3 h-3 ${crit.color}`} /> {crit.label}
                    </p>
                    <p className="text-xs font-semibold text-slate-200 mt-0.5">{crit.value}</p>
                  </div>
                ))}
              </div>

              {/* Atouts / Points de vigilance */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3" data-testid={`legal-form-highlights-${lf.id}`}>
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-mono text-emerald-300 flex items-center gap-1 mb-1">
                    <ThumbsUp className="w-3 h-3" /> Atouts
                  </p>
                  <ul className="space-y-1">
                    {hl.pros.map(p => (
                      <li key={p} className="text-xs text-slate-300 flex items-start gap-1.5">
                        <span className="text-emerald-400 mt-0.5 flex-shrink-0">✓</span> {p}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-mono text-amber-300 flex items-center gap-1 mb-1">
                    <AlertTriangle className="w-3 h-3" /> Points de vigilance
                  </p>
                  <ul className="space-y-1">
                    {hl.cons.map(c => (
                      <li key={c} className="text-xs text-slate-400 flex items-start gap-1.5">
                        <span className="text-amber-400 mt-0.5 flex-shrink-0">!</span> {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {isSelected && (
                <div className="mt-4 pt-3 border-t border-ink-600 text-xs text-slate-400 italic leading-relaxed" data-testid={`legal-form-teach-${lf.id}`}>
                  <BookOpen className="w-3.5 h-3.5 inline mr-1.5 text-amber-300" />
                  {lf.teachingNote}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Evolution forms — locked previews */}
      <div className="mt-4">
        <p className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-2">
          Disponibles par évolution (CA &gt; 188 700 € ou décision pédagogique)
        </p>
        <div className="grid grid-cols-2 gap-2 opacity-60">
          {evolutionForms.map(lf => (
            <div
              key={lf.id}
              className="rounded-md border border-ink-600 bg-ink-800/60 p-2.5 cursor-not-allowed"
              data-testid={`legal-form-locked-${lf.id}`}
              title="Disponible par évolution une fois votre société établie"
            >
              <div className="flex items-center gap-2">
                <span className="text-base">{lf.icon}</span>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-300">{lf.shortName}</p>
                  <p className="text-[10px] text-slate-500 truncate">{lf.fullLabel}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
